import React from 'react';
import Plus from 'lucide-react/dist/esm/icons/plus.js';
import Minus from 'lucide-react/dist/esm/icons/minus.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import Package from 'lucide-react/dist/esm/icons/package.js';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles.js';
import { usePurchasePage } from './PurchasePageContext';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit';
import { useContent } from '../../../contexts/ContentContext';
import { useTranslations } from '../../../contexts/TranslationContext';

export default function PurchaseTicketSelectorSection({ section, config }) {
  const {
    packages: apiPackages,
    packageQuantities,
    incrementPackage,
    decrementPackage,
    siteLoading,
  } = usePurchasePage();
  const { c } = useContent();
  const { t: tr } = useTranslations();

  const packages = apiPackages || [];
  const isLoading = siteLoading || !apiPackages;
  const isEmpty = !isLoading && packages.length === 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-3 sm:p-5 md:p-6 shadow-lg mb-2 sm:mb-6">
      <div className="flex items-center gap-2 mb-2 sm:mb-4">
        <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
          <InlineTranslationEdit
            translationKey="purchase.ticket_selector.title"
            value={c('purchase.ticket_selector.title', tr('purchase.ticket_selector.title') || 'Select Ticket Packages')}
            defaultText="Select Ticket Packages"
          />
        </h2>
      </div>

      <div className="space-y-2">
        {isLoading && (
          <div className="py-8 text-center text-gray-500 dark:text-gray-400 text-sm">
            <InlineTranslationEdit
              translationKey="purchase.ticket_selector.loading"
              value={c('purchase.ticket_selector.loading', tr('purchase.ticket_selector.loading') || 'Loading packages...')}
              defaultText="Loading packages..."
            />
          </div>
        )}
        {isEmpty && (
          <div className="py-8 text-center text-gray-500 dark:text-gray-400 text-sm">
            <InlineTranslationEdit
              translationKey="purchase.ticket_selector.empty"
              value={c('purchase.ticket_selector.empty', tr('purchase.ticket_selector.empty') || 'No ticket packages available right now.')}
              defaultText="No ticket packages available right now."
            />
          </div>
        )}
        {!isLoading && !isEmpty && packages.map((pkg) => {
          const quantity = packageQuantities[pkg.id] || 0;
          const isSelected = quantity > 0;

          return (
            <div
              key={pkg.id}
              onClick={() => incrementPackage(pkg.id)}
              className={`flex items-center justify-between gap-3 rounded-2xl p-2.5 sm:p-3.5 transition-all border cursor-pointer select-none ${
                isSelected
                  ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/20 shadow-md ring-1 ring-blue-500/30'
                  : 'border-gray-200 dark:border-gray-700/80 hover:border-blue-300 dark:hover:border-blue-500/50 bg-gray-50/60 dark:bg-gray-800/50'
              }`}
            >
              <div className="flex items-center gap-2.5 sm:gap-3.5 min-w-0">
                <div className={`
                  w-8 h-8 sm:w-10 sm:h-10 shrink-0 rounded-xl flex items-center justify-center font-black text-xs sm:text-base transition-all
                  ${isSelected ? 'bg-blue-600 text-white shadow-md' : 'bg-white dark:bg-gray-800 text-gray-500 border border-gray-200/80 dark:border-gray-700'}
                `}>
                  {pkg.tickets}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs sm:text-sm font-extrabold text-gray-900 dark:text-white">
                      {pkg.tickets}{' '}
                      <InlineTranslationEdit
                        translationKey={pkg.tickets === 1 ? 'purchase.ticket_selector.ticketSingular' : 'purchase.ticket_selector.ticketPlural'}
                        value={c(
                          pkg.tickets === 1 ? 'purchase.ticket_selector.ticketSingular' : 'purchase.ticket_selector.ticketPlural',
                          pkg.tickets === 1 ? (tr('purchase.ticket_selector.ticket') || 'Ticket') : (tr('purchase.ticket_selector.tickets') || 'Tickets')
                        )}
                        defaultText={pkg.tickets === 1 ? (tr('purchase.ticket_selector.ticket') || 'Ticket') : (tr('purchase.ticket_selector.tickets') || 'Tickets')}
                      />
                    </span>
                    {pkg.savings && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[8px] sm:text-[9px] font-black bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-sm uppercase tracking-wider border border-white/20 whitespace-nowrap">
                        <Sparkles className="w-2.5 h-2.5 fill-white" />
                        <InlineTranslationEdit
                          translationKey={pkg.savingsKey || 'purchase.ticket_selector.bestValue'}
                          value={c(pkg.savingsKey || 'purchase.ticket_selector.bestValue', tr(pkg.savingsKey || 'purchase.ticket_selector.bestValue') || pkg.savings)}
                          defaultText={pkg.savings}
                        />
                      </span>
                    )}
                  </div>
                  <div className="text-sm sm:text-lg font-black text-blue-600 dark:text-blue-400 leading-tight">
                    ${pkg.price}
                  </div>
                </div>
              </div>

              <div
                className="flex items-center gap-1.5 sm:gap-2 bg-gray-100 dark:bg-gray-900/60 p-1 rounded-xl border border-gray-200/80 dark:border-gray-700/80 shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => decrementPackage(pkg.id)}
                  disabled={quantity === 0}
                  className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center disabled:opacity-20 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all shadow-sm active:scale-95"
                  aria-label="Decrease quantity"
                >
                  <Minus className="w-3.5 h-3.5 text-gray-700 dark:text-gray-300" />
                </button>
                <span className="text-xs sm:text-sm font-black text-gray-900 dark:text-white tabular-nums tracking-tighter px-1">
                  {quantity}
                </span>
                <button
                  type="button"
                  onClick={() => incrementPackage(pkg.id)}
                  className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition-all shadow-md active:scale-95"
                  aria-label="Increase quantity"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
